import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import dayjs from 'dayjs';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { RemovedCoApplicant } from '@lmc-app/shared/interface/removed-co-applicant.interface';

export interface CoApplicantHistoryDialogData {
	// Whose RemovedCoApplicantHistory entries (any status) to show
	applicationId: string;
}

type TimelineEventKind = 'removed' | 'restored' | 'failed' | 'pending';

interface TimelineEvent {
	kind: TimelineEventKind;
	date: dayjs.Dayjs;
	dayKey: string;
	coApplicantName: string;
	doneBy: string;
	reason: string | null;
	errorMessage: string | null;
}

const EVENT_LABELS: Record<TimelineEventKind, string> = {
	removed: 'Removed',
	restored: 'Restored',
	failed: 'Removal failed',
	pending: 'Removal in progress',
};

const EVENT_ICONS: Record<TimelineEventKind, string> = {
	removed: 'person_remove',
	restored: 'restore',
	failed: 'error_outline',
	pending: 'hourglass_empty',
};

/**
 * Opened from "History" on the co-applicant tabs: every co-applicant removal and restore
 * on the application as a timeline, newest first, in the style of the Activities timeline
 */
@Component({
	selector: 'app-co-applicant-history-dialog',
	templateUrl: './co-applicant-history-dialog.component.html',
	styleUrls: ['./co-applicant-history-dialog.component.scss'],
	standalone: true,
	imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoApplicantHistoryDialogComponent implements OnInit, OnDestroy {
	readonly data = inject<CoApplicantHistoryDialogData>(MAT_DIALOG_DATA);
	private readonly applicationService = inject(ApplicationService);
	private readonly changeDetector = inject(ChangeDetectorRef);
	private historySub?: Subscription;

	readonly eventLabels = EVENT_LABELS;
	readonly eventIcons = EVENT_ICONS;

	isLoading = true;
	loadFailed = false;
	events: TimelineEvent[] = [];
	removalCount = 0;
	restoreCount = 0;

	// Fetched on open so removals and restores made since the page loaded are shown
	ngOnInit(): void {
		this.historySub = this.applicationService.getCoApplicantHistory(this.data.applicationId).subscribe({
			next: (history) => {
				this.events = this.toEvents(history);
				this.removalCount = this.events.filter((event) => event.kind === 'removed').length;
				this.restoreCount = this.events.filter((event) => event.kind === 'restored').length;
				this.isLoading = false;
				this.changeDetector.markForCheck();
			},
			error: (error) => {
				console.error(error);
				this.loadFailed = true;
				this.isLoading = false;
				this.changeDetector.markForCheck();
			},
		});
	}

	ngOnDestroy(): void {
		this.historySub?.unsubscribe();
	}

	// A history entry is its removal, plus its restore once restored
	private toEvents(history: RemovedCoApplicant[]): TimelineEvent[] {
		return history
			.reduce<TimelineEvent[]>((events, entry) => {
				const coApplicantName = entry.coApplicantName?.trim() || 'Unnamed co-applicant';
				const removalKind: TimelineEventKind =
					entry.status === 'FAILED' ? 'failed' : entry.status === 'PENDING' ? 'pending' : 'removed';
				events.push(
					this.toEvent(removalKind, entry.removedDate, coApplicantName, entry.removedBy, entry.removalReason, entry.errorMessage)
				);
				if (entry.restoredDate) {
					events.push(
						this.toEvent('restored', entry.restoredDate, coApplicantName, entry.restoredBy ?? '', entry.restoreReason, null)
					);
				}
				return events;
			}, [])
			.sort((a, b) => b.date.valueOf() - a.date.valueOf());
	}

	formatDay(date: dayjs.Dayjs): string {
		return date.format('dddd, MMM D, YYYY');
	}

	formatTime(date: dayjs.Dayjs): string {
		return date.format('hh:mm A');
	}

	formatDateTime(date: dayjs.Dayjs): string {
		return date.format('MMM D, YYYY hh:mm:ss A');
	}

	trackEvent(_index: number, event: TimelineEvent): string {
		return `${event.kind}-${event.date.valueOf()}-${event.coApplicantName}`;
	}

	private toEvent(
		kind: TimelineEventKind,
		value: string,
		coApplicantName: string,
		doneBy: string,
		reason: string | null,
		errorMessage: string | null
	): TimelineEvent {
		const date = dayjs(value);
		return {
			kind,
			date,
			dayKey: date.format('YYYY-MM-DD'),
			coApplicantName,
			doneBy: doneBy || 'Unknown user',
			reason,
			errorMessage,
		};
	}
}
